import React, { useState } from 'react'
import { X, Upload, FileText, AlertCircle, Loader } from 'lucide-react'
import api from '../../services/api'

interface UploadResourceModalProps {
  onClose: () => void
  onSuccess: () => void
}

const UploadResourceModal: React.FC<UploadResourceModalProps> = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [content, setContent] = useState('')
  const [resourceType, setResourceType] = useState('document')
  const [tags, setTags] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // File upload
  const [file, setFile] = useState<File | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
      if (!name) {
        setName(selectedFile.name.replace(/\.[^/.]+$/, ''))
      }

      // Read file content
      const reader = new FileReader()
      reader.onload = (event) => {
        const text = event.target?.result as string
        setContent(text)
      }
      reader.readAsText(selectedFile)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!name.trim()) {
      setError('El nombre es requerido')
      return
    }

    if (!content.trim() || content.length < 10) {
      setError('El contenido debe tener al menos 10 caracteres')
      return
    }

    try {
      setLoading(true)

      const tagsArray = tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0)

      await api.resources.upload({
        name: name.trim(),
        description: description.trim() || undefined,
        content: content.trim(),
        resource_type: resourceType,
        mime_type: 'text/plain',
        tags: tagsArray.length > 0 ? tagsArray : undefined
      })

      onSuccess()
    } catch (err: any) {
      console.error('Error uploading resource:', err)
      setError(err.response?.data?.error || 'Error al subir el recurso')
    } finally {
      setLoading(false)
    }
  }

  const estimatedTokens = Math.ceil(content.length / 4)
  const willBeChunked = estimatedTokens >= 100000

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Upload className="text-blue-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">Subir Recurso Inteligente</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archivo de Texto
            </label>
            <div className="flex items-center gap-4">
              <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors cursor-pointer">
                <FileText size={20} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  {file ? file.name : 'Seleccionar archivo .txt'}
                </span>
                <input
                  type="file"
                  accept=".txt,.md,.text"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              O escribe/pega el contenido directamente abajo
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Recurso *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Documentación de n8n"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Breve descripción del contenido"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Recurso
            </label>
            <select
              value={resourceType}
              onChange={(e) => setResourceType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="document">Documento</option>
              <option value="manual">Manual</option>
              <option value="knowledge-base">Base de Conocimiento</option>
              <option value="code">Código</option>
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (separados por comas)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Ej: n8n, automation, docs"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contenido *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Pega o escribe el contenido del documento aquí..."
              rows={12}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              required
            />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                {content.length.toLocaleString()} caracteres • ~{estimatedTokens.toLocaleString()} tokens
              </p>
              {willBeChunked && (
                <span className="text-xs text-purple-600 font-medium">
                  Se usará chunking + búsqueda semántica
                </span>
              )}
              {!willBeChunked && content.length > 0 && (
                <span className="text-xs text-green-600 font-medium">
                  Se usará contexto completo
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Sistema RAG con Granite + TOON</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Documentos pequeños (&lt;100K tokens): Contexto completo con Granite</li>
              <li>• Documentos grandes (≥100K tokens): Chunking inteligente + búsqueda semántica</li>
              <li>• TOON reduce tokens en ~40% para optimizar costos</li>
              <li>• Respuestas con citas y nivel de confianza</li>
            </ul>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !name.trim() || !content.trim() || content.length < 10}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                Subiendo...
              </>
            ) : (
              <>
                <Upload size={20} />
                Subir Recurso
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UploadResourceModal
